import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './authContext';

interface UserRoleContextType {
  userRole: string | null;
  isAdmin: boolean;
  isLoading: boolean;
  accountInfos: any;
}

const UserRoleContext = createContext<UserRoleContextType>({
  userRole: null,
  isAdmin: false,
  isLoading: true,
  accountInfos: null
});

const ROLE_STORAGE_KEY = 'poeckes.userRole';

const getStoredRole = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const role = window.localStorage.getItem(ROLE_STORAGE_KEY);
  return role && role.trim().length > 0 ? role : null;
};

const normalizeRole = (role: string | null): string | null => {
  if (!role) return null;

  switch (role.toLowerCase()) {
    case 'user':
      return 'User';
    case 'foreman':
      return 'Foreman';
    case 'hr':
      return 'HR';
    case 'admin':
      return 'Admin';
    default:
      return role;
  }
};

export const UserRoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, claims, account, isLoading: authLoading, redirectProcessing } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(() => normalizeRole(getStoredRole()));
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(() => {
    const storedRole = normalizeRole(getStoredRole());
    return Boolean(storedRole && ['admin', 'hr'].includes(storedRole.toLowerCase()));
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (authLoading || redirectProcessing) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(ROLE_STORAGE_KEY);
      }
      setUserRole(null);
      setIsUserAdmin(false);
      setIsLoading(false);
      return;
    }

    const claimsRoles = Array.isArray(claims?.roles) ? claims.roles : [];
    const accountRoles = Array.isArray((account as any)?.idTokenClaims?.roles)
      ? (account as any).idTokenClaims.roles
      : [];
    const storedRole = normalizeRole(getStoredRole());

    const resolvedRole = normalizeRole(claimsRoles[0] || accountRoles[0] || storedRole || null);

    if (resolvedRole && typeof window !== 'undefined') {
      window.localStorage.setItem(ROLE_STORAGE_KEY, resolvedRole);
    }

    setUserRole(resolvedRole);
    setIsUserAdmin(Boolean(resolvedRole && ['admin', 'hr'].includes(resolvedRole.toLowerCase())));
    setIsLoading(false);
  }, [authLoading, redirectProcessing, isAuthenticated, claims, account]);

  return (
    <UserRoleContext.Provider value={{ 
      userRole, 
      isAdmin: isUserAdmin,
      isLoading,
      accountInfos: account,
    }}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = () => useContext(UserRoleContext);
